import { memo, type ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { cn } from '@/lib/utils'

import type { SeatInfo } from './billing-seat.interface'
import { BillingSeatManager } from './billing-seat-manager.molecule'

export interface BillingEarlyAccessPlanCardProps {
  seatCount: { used: number; allowed: number }
  seatInfo?: SeatInfo | null
  isAddingSeats?: boolean
  isRemovingSeats?: boolean
  onAddSeats?: (quantity: number) => void
  onRemoveSeats?: (quantity: number) => void
  className?: string
}

const BillingEarlyAccessPlanCardBase: React.FC<BillingEarlyAccessPlanCardProps> = ({
  seatCount,
  seatInfo,
  isAddingSeats,
  isRemovingSeats,
  onAddSeats,
  onRemoveSeats,
  className,
}): ReactNode => {
  const showSeatManager = seatInfo != null && onAddSeats != null && onRemoveSeats != null

  return (
    <Card className={cn('billing-early-access-plan-card', className)}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <p className="text-3xl font-bold">Pro</p>
          <Badge variant="outline">Early Access</Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Full Pro features are unlocked while we finalize subscriptions. No payment required.
        </p>
      </CardHeader>
      <CardContent>
        {showSeatManager ? (
          <BillingSeatManager
            seatInfo={seatInfo}
            isAddingSeats={isAddingSeats}
            isRemovingSeats={isRemovingSeats}
            onAddSeats={onAddSeats}
            onRemoveSeats={onRemoveSeats}
            hideSeatPrice
          />
        ) : (
          <ReadOnlySeatCount used={seatCount.used} allowed={seatCount.allowed} />
        )}
      </CardContent>
    </Card>
  )
}

const ReadOnlySeatCount: React.FC<{ used: number; allowed: number }> = ({
  used,
  allowed,
}): ReactNode => (
  <p className="text-sm text-muted-foreground">
    {used} / {allowed} {allowed === 1 ? 'seat' : 'seats'} used
  </p>
)

export const BillingEarlyAccessPlanCard = memo(BillingEarlyAccessPlanCardBase)

BillingEarlyAccessPlanCard.displayName = 'BillingEarlyAccessPlanCard'
